import { Voucher } from "@/lib/accounting/types";
import { renderUPISection } from "./UPIPaymentSection";
import { renderGSTStandardTemplate } from "./GSTStandardTemplate";

export type InvoiceTemplate = "classic" | "modern" | "minimal" | "professional" | "gst_standard";

export const TEMPLATE_OPTIONS: { id: InvoiceTemplate; name: string; description: string }[] = [
  { id: "gst_standard", name: "GST Standard", description: "Traditional Indian GST Tax Invoice with full details" },
  { id: "classic", name: "Classic", description: "Traditional Indian tax invoice layout" },
  { id: "modern", name: "Modern", description: "Clean, contemporary design with accent colors" },
  { id: "minimal", name: "Minimal", description: "Simple and distraction-free" },
  { id: "professional", name: "Professional", description: "Formal layout with borders and structure" },
];

interface CompanyInfo {
  name: string;
  address: string;
  gstin: string;
  state: string;
  phone: string;
  email: string;
}

const DEFAULT_COMPANY: CompanyInfo = {
  name: "hSECURITIES",
  address: "GT Road, Mandi Gobindgarh, Punjab - 147301",
  gstin: "22FZVPS1799R5",
  state: "Punjab",
  phone: "+91 98140 12345",
  email: "billing@hsecurities.com",
};

function numberToWords(num: number): string {
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convert = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " and " + convert(n % 100) : "");
    if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
    if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
    return convert(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + convert(n % 10000000) : "");
  };

  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  let result = "Rupees " + convert(rupees);
  if (paise > 0) result += " and " + convert(paise) + " Paise";
  return result + " Only";
}

export function renderInvoiceHTML(voucher: Voucher, template: InvoiceTemplate): string {
  const company = DEFAULT_COMPANY;
  const invoiceDate = new Date(voucher.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const isSales = voucher.type === "Sales" || voucher.type === "Credit Note";
  const docTitle = voucher.type === "Credit Note" ? "Credit Note" : voucher.type === "Debit Note" ? "Debit Note" : voucher.type === "Sales" ? "Tax Invoice" : "Purchase Invoice";

  const taxableAmount = voucher.totalDebit - (voucher.gstAmount || 0);
  const amountInWords = numberToWords(voucher.totalDebit);

  const hasRealItems = Array.isArray(voucher.items) && voucher.items.length > 0;
  const displayItems = hasRealItems
    ? voucher.items!
    : voucher.lines
        .filter(l => l.debit > 0 === isSales ? true : l.credit > 0)
        .filter(l => !l.ledgerName.includes("CGST") && !l.ledgerName.includes("SGST") && !l.ledgerName.includes("IGST"))
        .filter(l => l.ledgerName !== voucher.partyName)
        .map(l => ({
          name: l.narration || l.ledgerName,
          description: l.narration || l.ledgerName,
          hsnCode: "—",
          qty: 1,
          unit: "Nos",
          rate: isSales ? l.credit : l.debit,
          amount: isSales ? l.credit : l.debit
        }));

  const itemRows = displayItems.map((item, i) => `
      <tr>
        <td style="padding:8px;border:1px solid #ddd;text-align:center">${i + 1}</td>
        <td style="padding:8px;border:1px solid #ddd"><strong>${item.name || item.description}</strong>${item.description && item.description !== item.name ? `<br><small style="color:#666">${item.description}</small>` : ''}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:center">${item.hsnCode || "—"}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:center">${item.qty || 1} ${item.unit || "Pcs"}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:right">₹${(Number(item.rate) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        <td style="padding:8px;border:1px solid #ddd;text-align:right">₹${(Number(item.amount) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
      </tr>
    `).join("");

  const styles: Record<InvoiceTemplate, { accent: string; bg: string; headerBg: string; font: string }> = {
    gst_standard: { accent: "#000", bg: "#fff", headerBg: "#f5f5f5", font: "'Segoe UI', Tahoma, sans-serif" },
    classic: { accent: "#1a365d", bg: "#fff", headerBg: "#1a365d", font: "Georgia, serif" },
    modern: { accent: "#0891b2", bg: "#fafafa", headerBg: "#0891b2", font: "'Inter', sans-serif" },
    minimal: { accent: "#374151", bg: "#fff", headerBg: "#f9fafb", font: "'Inter', sans-serif" },
    professional: { accent: "#1e3a5f", bg: "#fff", headerBg: "#f0f4f8", font: "'Segoe UI', sans-serif" },
  };

  const s = styles[template];

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${docTitle}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: ${s.font}; background: ${s.bg}; color: #1a202c; }
  .invoice { max-width: 800px; margin: 0 auto; padding: 32px; }
  @media print { body { background: white; } .invoice { padding: 16px; } .no-print { display: none !important; } }
</style>
</head><body>
<div class="invoice">
  ${template === "gst_standard" ? renderGSTStandardTemplate(company, voucher, docTitle, invoiceDate, taxableAmount, amountInWords) : ""}
  ${template === "classic" ? renderClassicTemplate(company, voucher, docTitle, invoiceDate, itemRows, taxableAmount, amountInWords, s) : ""}
  ${template === "modern" ? renderModernTemplate(company, voucher, docTitle, invoiceDate, itemRows, taxableAmount, amountInWords, s) : ""}
  ${template === "minimal" ? renderMinimalTemplate(company, voucher, docTitle, invoiceDate, itemRows, taxableAmount, amountInWords, s) : ""}
  ${template === "professional" ? renderProfessionalTemplate(company, voucher, docTitle, invoiceDate, itemRows, taxableAmount, amountInWords, s) : ""}
</div>
</body></html>`;
}

function renderClassicTemplate(c: CompanyInfo, v: Voucher, title: string, date: string, rows: string, taxable: number, words: string, s: { accent: string; headerBg: string }) {
  return `
    <div style="border:2px solid ${s.accent};padding:0">
      <div style="background:${s.headerBg};color:white;padding:20px 24px;display:flex;justify-content:space-between;align-items:center">
        <div>
          <h1 style="font-size:22px;font-weight:bold;margin-bottom:4px">${c.name}</h1>
          <p style="font-size:12px;opacity:0.9">${c.address}</p>
          <p style="font-size:12px;opacity:0.9">GSTIN: ${c.gstin}</p>
        </div>
        <div style="text-align:right">
          <h2 style="font-size:18px;font-weight:bold;text-transform:uppercase;letter-spacing:2px">${title}</h2>
          <p style="font-size:11px;opacity:0.8;margin-top:4px">Original for Recipient</p>
        </div>
      </div>
      <div style="padding:16px 24px;display:flex;justify-content:space-between;background:#f7fafc;border-bottom:1px solid #e2e8f0">
        <div>
          <p style="font-size:11px;color:#718096;text-transform:uppercase">Invoice No</p>
          <p style="font-weight:600">${v.invoiceNumber || v.voucherNumber}</p>
        </div>
        <div>
          <p style="font-size:11px;color:#718096;text-transform:uppercase">Date</p>
          <p style="font-weight:600">${date}</p>
        </div>
        <div>
          <p style="font-size:11px;color:#718096;text-transform:uppercase">Voucher No</p>
          <p style="font-weight:600">${v.voucherNumber}</p>
        </div>
      </div>
      <div style="padding:16px 24px;border-bottom:1px solid #e2e8f0">
        <p style="font-size:11px;color:#718096;text-transform:uppercase;margin-bottom:4px">Bill To</p>
        <p style="font-weight:600;font-size:15px">${v.partyName || "—"}</p>
      </div>
      <div style="padding:16px 24px">
        <table style="width:100%;border-collapse:collapse;font-size:13px">
          <thead>
            <tr style="background:${s.headerBg}">
              <th style="padding:10px 8px;border:1px solid #ddd;color:white;text-align:center;width:35px">#</th>
              <th style="padding:10px 8px;border:1px solid #ddd;color:white;text-align:left">Description of Goods / Services</th>
              <th style="padding:10px 8px;border:1px solid #ddd;color:white;text-align:center;width:75px">HSN/SAC</th>
              <th style="padding:10px 8px;border:1px solid #ddd;color:white;text-align:center;width:65px">Qty</th>
              <th style="padding:10px 8px;border:1px solid #ddd;color:white;text-align:right;width:85px">Rate (₹)</th>
              <th style="padding:10px 8px;border:1px solid #ddd;color:white;text-align:right;width:95px">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div style="padding:16px 24px;border-top:1px solid #e2e8f0">
        <div style="display:flex;justify-content:flex-end">
          <div style="width:280px;font-size:13px">
            <div style="display:flex;justify-content:space-between;padding:4px 0"><span>Taxable Amount</span><span>₹${taxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            ${v.cgst ? `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>CGST</span><span>₹${v.cgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>` : ""}
            ${v.sgst ? `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>SGST</span><span>₹${v.sgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>` : ""}
            ${v.igst ? `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>IGST</span><span>₹${v.igst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>` : ""}
            <div style="display:flex;justify-content:space-between;padding:8px 0;border-top:2px solid ${s.accent};font-weight:bold;font-size:15px"><span>Total</span><span>₹${v.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
          </div>
        </div>
        <p style="font-size:11px;color:#718096;margin-top:12px"><strong>Amount in Words:</strong> ${words}</p>
      </div>
      ${(v.type === "Sales" || v.type === "Debit Note") ? renderUPISection(v.totalDebit, v.invoiceNumber || v.voucherNumber) : ""}
      <div style="padding:16px 24px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:flex-end">
        <div style="font-size:11px;color:#718096">
          <p>Terms & Conditions:</p>
          <p>1. Payment due within 30 days</p>
          <p>2. Subject to ${c.state} jurisdiction</p>
        </div>
        <div style="text-align:center">
          <div style="width:150px;border-bottom:1px solid #aaa;margin-bottom:4px;height:40px"></div>
          <p style="font-size:11px;color:#718096">Authorized Signatory</p>
        </div>
      </div>
    </div>`;
}

function renderModernTemplate(c: CompanyInfo, v: Voucher, title: string, date: string, rows: string, taxable: number, words: string, s: { accent: string }) {
  return `
    <div style="padding:32px">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px">
        <div>
          <h1 style="font-size:28px;font-weight:700;color:${s.accent};margin-bottom:8px">${c.name}</h1>
          <p style="font-size:12px;color:#64748b">${c.address}</p>
          <p style="font-size:12px;color:#64748b">GSTIN: ${c.gstin} | ${c.phone}</p>
        </div>
        <div style="text-align:right">
          <div style="background:${s.accent};color:white;padding:8px 20px;border-radius:8px;display:inline-block;margin-bottom:8px">
            <span style="font-size:16px;font-weight:600;letter-spacing:1px">${title.toUpperCase()}</span>
          </div>
          <p style="font-size:13px;color:#475569"><strong>${v.invoiceNumber || v.voucherNumber}</strong></p>
          <p style="font-size:12px;color:#94a3b8">${date}</p>
        </div>
      </div>
      <div style="background:#f8fafc;border-radius:8px;padding:16px 20px;margin-bottom:24px;border-left:4px solid ${s.accent}">
        <p style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Billed To</p>
        <p style="font-weight:600;font-size:15px;color:#1e293b">${v.partyName || "—"}</p>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:24px">
        <thead>
          <tr>
            <th style="padding:12px 8px;border-bottom:2px solid ${s.accent};text-align:center;color:${s.accent};font-size:11px;text-transform:uppercase;width:35px">#</th>
            <th style="padding:12px 8px;border-bottom:2px solid ${s.accent};text-align:left;color:${s.accent};font-size:11px;text-transform:uppercase">Description of Goods / Services</th>
            <th style="padding:12px 8px;border-bottom:2px solid ${s.accent};text-align:center;color:${s.accent};font-size:11px;text-transform:uppercase;width:75px">HSN</th>
            <th style="padding:12px 8px;border-bottom:2px solid ${s.accent};text-align:center;color:${s.accent};font-size:11px;text-transform:uppercase;width:65px">Qty</th>
            <th style="padding:12px 8px;border-bottom:2px solid ${s.accent};text-align:right;color:${s.accent};font-size:11px;text-transform:uppercase;width:85px">Rate</th>
            <th style="padding:12px 8px;border-bottom:2px solid ${s.accent};text-align:right;color:${s.accent};font-size:11px;text-transform:uppercase;width:95px">Amount</th>
          </tr>
        </thead>
        <tbody>${rows.replace(/border:1px solid #ddd/g, "border-bottom:1px solid #e2e8f0;border-left:none;border-right:none;border-top:none")}</tbody>
      </table>
      <div style="display:flex;justify-content:flex-end;margin-bottom:20px">
        <div style="width:280px;background:#f8fafc;border-radius:8px;padding:16px;font-size:13px">
          <div style="display:flex;justify-content:space-between;padding:4px 0;color:#64748b"><span>Subtotal</span><span>₹${taxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
          ${v.cgst ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#64748b"><span>CGST</span><span>₹${v.cgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>` : ""}
          ${v.sgst ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#64748b"><span>SGST</span><span>₹${v.sgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>` : ""}
          ${v.igst ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#64748b"><span>IGST</span><span>₹${v.igst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>` : ""}
          <div style="display:flex;justify-content:space-between;padding:10px 0 4px;border-top:2px solid ${s.accent};font-weight:700;font-size:16px;color:${s.accent}"><span>Total</span><span>₹${v.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
        </div>
      </div>
      ${(v.type === "Sales" || v.type === "Debit Note") ? renderUPISection(v.totalDebit, v.invoiceNumber || v.voucherNumber) : ""}
      <p style="font-size:11px;color:#94a3b8"><strong>In Words:</strong> ${words}</p>
    </div>`;
}

function renderMinimalTemplate(c: CompanyInfo, v: Voucher, title: string, date: string, rows: string, taxable: number, words: string, _s: { accent: string }) {
  return `
    <div style="padding:40px;max-width:700px;margin:0 auto">
      <div style="margin-bottom:40px">
        <h1 style="font-size:11px;text-transform:uppercase;letter-spacing:3px;color:#9ca3af;margin-bottom:24px">${title}</h1>
        <h2 style="font-size:24px;font-weight:700;color:#111827;margin-bottom:4px">${c.name}</h2>
        <p style="font-size:12px;color:#6b7280">${c.gstin}</p>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:32px;font-size:13px">
        <div>
          <p style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">To</p>
          <p style="font-weight:600;color:#111827">${v.partyName || "—"}</p>
        </div>
        <div style="text-align:right">
          <p style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Invoice</p>
          <p style="font-weight:600;color:#111827">${v.invoiceNumber || v.voucherNumber}</p>
          <p style="color:#6b7280;font-size:12px">${date}</p>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:32px">
        <thead>
          <tr>
            <th style="padding:8px 0;border-bottom:1px solid #e5e7eb;text-align:left;color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:1px">Item</th>
            <th style="padding:8px 0;border-bottom:1px solid #e5e7eb;text-align:right;color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:1px">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${v.lines.filter(l => !l.ledgerName.includes("CGST") && !l.ledgerName.includes("SGST") && !l.ledgerName.includes("IGST") && l.ledgerName !== v.partyName).map(l => `
            <tr><td style="padding:10px 0;border-bottom:1px solid #f3f4f6">${l.ledgerName}</td>
            <td style="padding:10px 0;border-bottom:1px solid #f3f4f6;text-align:right">₹${(l.debit || l.credit).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td></tr>
          `).join("")}
        </tbody>
      </table>
      <div style="text-align:right;font-size:13px;margin-bottom:24px">
        <p style="color:#6b7280;margin-bottom:4px">Subtotal: ₹${taxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
        ${v.gstAmount ? `<p style="color:#6b7280;margin-bottom:4px">Tax: ₹${v.gstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>` : ""}
        <p style="font-size:20px;font-weight:700;color:#111827;margin-top:8px">₹${v.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
      </div>
      ${(v.type === "Sales" || v.type === "Debit Note") ? renderUPISection(v.totalDebit, v.invoiceNumber || v.voucherNumber) : ""}
      <p style="font-size:10px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:16px">${words}</p>
    </div>`;
}

function renderProfessionalTemplate(c: CompanyInfo, v: Voucher, title: string, date: string, rows: string, taxable: number, words: string, s: { accent: string; headerBg: string }) {
  return `
    <div style="border:1px solid #cbd5e1;border-radius:4px">
      <div style="background:${s.headerBg};padding:20px 24px;border-bottom:3px solid ${s.accent}">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <h1 style="font-size:20px;font-weight:700;color:${s.accent}">${c.name}</h1>
          <h2 style="font-size:16px;font-weight:600;color:${s.accent};text-transform:uppercase;letter-spacing:2px">${title}</h2>
        </div>
        <p style="font-size:11px;color:#64748b;margin-top:4px">${c.address} | GSTIN: ${c.gstin}</p>
      </div>
      <div style="padding:16px 24px;display:grid;grid-template-columns:1fr 1fr;gap:20px;border-bottom:1px solid #e2e8f0">
        <div>
          <p style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Bill To</p>
          <p style="font-weight:600;font-size:14px;color:#1e293b">${v.partyName || "—"}</p>
        </div>
        <div style="text-align:right">
          <p style="font-size:12px;color:#64748b">No: <strong style="color:#1e293b">${v.invoiceNumber || v.voucherNumber}</strong></p>
          <p style="font-size:12px;color:#64748b">Date: <strong style="color:#1e293b">${date}</strong></p>
        </div>
      </div>
      <div style="padding:16px 24px">
        <table style="width:100%;border-collapse:collapse;font-size:13px">
          <thead>
            <tr style="background:#f1f5f9">
              <th style="padding:10px 8px;border:1px solid #e2e8f0;text-align:center;color:${s.accent};font-size:11px;font-weight:600;width:35px">#</th>
              <th style="padding:10px 8px;border:1px solid #e2e8f0;text-align:left;color:${s.accent};font-size:11px;font-weight:600">Description of Goods / Services</th>
              <th style="padding:10px 8px;border:1px solid #e2e8f0;text-align:center;color:${s.accent};font-size:11px;font-weight:600;width:75px">HSN</th>
              <th style="padding:10px 8px;border:1px solid #e2e8f0;text-align:center;color:${s.accent};font-size:11px;font-weight:600;width:65px">Qty</th>
              <th style="padding:10px 8px;border:1px solid #e2e8f0;text-align:right;color:${s.accent};font-size:11px;font-weight:600;width:85px">Rate</th>
              <th style="padding:10px 8px;border:1px solid #e2e8f0;text-align:right;color:${s.accent};font-size:11px;font-weight:600;width:95px">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div style="padding:16px 24px;border-top:1px solid #e2e8f0">
        <div style="display:flex;justify-content:flex-end">
          <table style="width:260px;font-size:13px;border-collapse:collapse">
            <tr><td style="padding:4px 0;color:#64748b">Taxable</td><td style="padding:4px 0;text-align:right">₹${taxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td></tr>
            ${v.cgst ? `<tr><td style="padding:4px 0;color:#64748b">CGST</td><td style="padding:4px 0;text-align:right">₹${v.cgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td></tr>` : ""}
            ${v.sgst ? `<tr><td style="padding:4px 0;color:#64748b">SGST</td><td style="padding:4px 0;text-align:right">₹${v.sgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td></tr>` : ""}
            ${v.igst ? `<tr><td style="padding:4px 0;color:#64748b">IGST</td><td style="padding:4px 0;text-align:right">₹${v.igst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td></tr>` : ""}
            <tr style="font-weight:700;border-top:2px solid ${s.accent}"><td style="padding:8px 0;color:${s.accent}">Total</td><td style="padding:8px 0;text-align:right;font-size:15px;color:${s.accent}">₹${v.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td></tr>
          </table>
        </div>
        <p style="font-size:10px;color:#94a3b8;margin-top:12px"><strong>In Words:</strong> ${words}</p>
      </div>
      ${(v.type === "Sales" || v.type === "Debit Note") ? renderUPISection(v.totalDebit, v.invoiceNumber || v.voucherNumber) : ""}
      <div style="padding:12px 24px;background:#f8fafc;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8">
        <span>This is a computer-generated document</span>
        <span>For ${c.name}</span>
      </div>
    </div>`;
}
